import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Heart, Plus, Search, Loader2, ArrowRight, Sparkles, BadgeCheck, ArrowLeft, Trash2, Edit3, Share2 } from "lucide-react";
import PhotoDrop from "@/components/PhotoDrop";

const PCT = (raised, goal) => goal ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

export function CharitiesList() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(""); const [q, setQ] = useState("");
  useEffect(() => { api.get("/charities/categories").then(r=>setCats(r.data.categories||[])); }, []);
  useEffect(() => {
    const p = new URLSearchParams();
    if (cat) p.set("category", cat);
    if (q) p.set("q", q);
    api.get(`/charities?${p.toString()}`).then(r=>setItems(r.data||[])).catch(e=>toast.error(formatErr(e)));
  }, [cat, q]);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="charities-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Charities</p>
          <h1 className="h-display text-4xl">Give any amount. Change something.</h1>
          <p className="text-[#4A4A4A] mt-1">Donors pick the amount. A 2 % Hoomanz Tip is added on top — the cause receives 100 %.</p>
        </div>
        {user && <Link to="/charities/new" className="btn-primary" data-testid="charities-new-btn"><Plus size={14}/> Post a cause</Link>}
      </div>
      <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-2" data-testid="charities-filters">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search causes…" className="input !pl-9" data-testid="charities-search"/>
        </div>
        <select className="input" value={cat} onChange={(e)=>setCat(e.target.value)} data-testid="charities-category">
          <option value="">All categories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {items === null && <p className="text-[#8A8A8A] mt-8">Loading…</p>}
      {items && items.length === 0 && <div className="card p-12 text-center mt-8"><Sparkles size={22} className="mx-auto text-[#0057FF] mb-2"/><p className="text-[#4A4A4A]">No open causes yet. Be the first to post one.</p></div>}
      <ul className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="charities-list">
        {(items || []).map(c => (
          <li key={c.id} className="card p-0 overflow-hidden hover:border-[#0A0A0A] transition-colors" data-testid={`charity-card-${c.id}`}>
            <Link to={`/charities/${c.id}`} className="block">
              {c.image ? <div className="aspect-[16/9] bg-[#F2F2F0]"><img src={c.image} alt="" className="w-full h-full object-cover"/></div>
                       : <div className="aspect-[16/9] bg-gradient-to-br from-[#F2F2F0] to-[#E8E8E6] flex items-center justify-center"><Heart size={28} className="text-[#8A8A8A]"/></div>}
              <div className="p-4">
                <div className="text-[11px] text-[#8A8A8A]"><span className="badge-soft">{c.category}</span></div>
                <p className="text-[15px] font-bold text-[#0A0A0A] mt-1.5 line-clamp-2">{c.title}</p>
                {c.goal_amount ? (
                  <div className="mt-3">
                    <div className="h-1.5 bg-[#F2F2F0] rounded-full overflow-hidden">
                      <div className="h-full bg-[#0057FF]" style={{ width: `${PCT(c.raised_amount, c.goal_amount)}%` }}/>
                    </div>
                    <p className="text-xs text-[#4A4A4A] mt-1.5">
                      <b>${(c.raised_amount||0).toFixed(0)}</b> raised of ${c.goal_amount.toFixed(0)} · {c.donor_count||0} donor{c.donor_count===1?"":"s"}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[#4A4A4A] mt-3"><b>${(c.raised_amount||0).toFixed(0)}</b> raised · {c.donor_count||0} donor{c.donor_count===1?"":"s"}</p>
                )}
                {c.owner?.name && <p className="text-[11px] text-[#8A8A8A] mt-2">by {c.owner.name}</p>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CharityCreate() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ title: "", description: "", category: "Humanitarian", goal_amount: "", currency: "USD", image: "", location: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/charities/categories").then(r=>setCats(r.data.categories||[])); }, []);
  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give it a title");
    setBusy(true);
    try {
      const { data } = await api.post("/charities", { ...f, goal_amount: f.goal_amount ? Number(f.goal_amount) : null });
      toast.success("Cause posted!");
      nav(`/charities/${data.id}`);
    } catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="charity-create-form">
      <div><p className="overline">New cause</p><h1 className="h-display text-4xl mt-1">Rally people around what matters.</h1></div>
      <div className="card p-6 space-y-4">
        <div><label className="text-sm font-medium">Title *</label><input className="input mt-1.5" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} data-testid="charity-title-input"/></div>
        <div><label className="text-sm font-medium">Description</label><textarea rows={5} className="input mt-1.5 resize-none" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} data-testid="charity-desc-input"/></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium">Category</label><select className="input mt-1.5" value={f.category} onChange={(e)=>setF({...f,category:e.target.value})} data-testid="charity-category-input">{cats.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className="text-sm font-medium">Location (optional)</label><input className="input mt-1.5" value={f.location} onChange={(e)=>setF({...f,location:e.target.value})} placeholder="City / country"/></div>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3">
          <div><label className="text-sm font-medium">Goal amount (optional)</label><input type="number" min="0" className="input mt-1.5" value={f.goal_amount} onChange={(e)=>setF({...f,goal_amount:e.target.value})} placeholder="e.g. 10000" data-testid="charity-goal-input"/></div>
          <div><label className="text-sm font-medium">Currency</label><select className="input mt-1.5" value={f.currency} onChange={(e)=>setF({...f,currency:e.target.value})}>{["USD","EUR","GBP","BRL","CAD","AUD"].map(c => <option key={c}>{c}</option>)}</select></div>
        </div>
        <div><label className="text-sm font-medium">Cover image (optional)</label><div className="mt-1.5"><PhotoDrop value={f.image} onChange={(v)=>setF({...f,image:v})} aspect="cover" testid="charity-image"/></div></div>
      </div>
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="charity-submit">{busy ? <Loader2 size={14} className="animate-spin"/> : <><Heart size={14}/> Post cause <ArrowRight size={14}/></>}</button>
    </form>
  );
}

export function CharityView() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null); const [err, setErr] = useState("");
  const [amount, setAmount] = useState("25");
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/charities/${id}`).then(r=>setC(r.data)).catch(e=>setErr(formatErr(e))); }, [id]);
  const donate = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt < 1) return toast.error("Amount must be at least $1");
    if (!user && (!email.trim() || !/^\S+@\S+\.\S+$/.test(email))) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const body = { charity_id: id, amount: amt, origin_url: window.location.origin };
      if (!user) { body.guest_email = email.trim(); body.guest_name = name.trim(); }
      const { data } = await api.post("/charities/donate", body);
      window.location.href = data.url;
    } catch (e2) { toast.error(formatErr(e2)); setBusy(false); }
  };
  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) return navigator.share({ title: c?.title, url }); await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { /* noop */ }
  };
  const remove = async () => {
    if (!confirm("Delete this cause?")) return;
    try { await api.delete(`/charities/${id}`); toast.success("Deleted"); nav("/charities"); } catch (e) { toast.error(formatErr(e)); }
  };
  if (err) return <div className="max-w-lg mx-auto p-16 text-center"><p className="text-[#B84B4B]">{err}</p><Link to="/charities" className="btn-ghost inline-flex mt-4"><ArrowLeft size={14}/> Back</Link></div>;
  if (!c) return <div className="p-16 text-center text-[#8A8A8A]"><Loader2 size={16} className="animate-spin inline"/> Loading…</div>;
  const isOwn = user && user.id === c.owner_id;
  const pct = c.goal_amount ? Math.min(100, Math.round(((c.raised_amount||0)/c.goal_amount)*100)) : 0;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="charity-view">
      <Link to="/charities" className="text-xs text-[#8A8A8A] hover:text-[#0A0A0A] inline-flex items-center gap-1 mb-4"><ArrowLeft size={12}/> All causes</Link>
      {c.image && <div className="rounded-3xl overflow-hidden bg-[#F2F2F0] aspect-[3/1] mb-6"><img src={c.image} alt="" className="w-full h-full object-cover"/></div>}
      <p className="overline mb-2 inline-flex items-center gap-1.5"><Heart size={11}/> Charity · {c.category}</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-tight" data-testid="charity-title">{c.title}</h1>
      {c.owner && <p className="text-sm text-[#4A4A4A] mt-2">by <Link to={`/u/${c.owner.id}`} className="hover:underline">{c.owner.name}</Link>{c.owner.verified && <BadgeCheck size={12} className="inline ml-1 text-[#0057FF]"/>}</p>}
      {c.description && <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-[#0A0A0A] mt-6">{c.description}</p>}
      <div className="card p-6 mt-8" data-testid="charity-progress">
        {c.goal_amount ? (
          <>
            <div className="flex items-baseline justify-between">
              <p><span className="font-heading text-3xl">${(c.raised_amount||0).toFixed(0)}</span> <span className="text-[#8A8A8A]">of ${c.goal_amount.toFixed(0)}</span></p>
              <p className="text-sm text-[#4A4A4A]">{pct}% · {c.donor_count||0} donors</p>
            </div>
            <div className="h-2 bg-[#F2F2F0] rounded-full overflow-hidden mt-3"><div className="h-full bg-[#0057FF]" style={{ width: `${pct}%` }}/></div>
          </>
        ) : (
          <p><span className="font-heading text-3xl">${(c.raised_amount||0).toFixed(0)}</span> raised · {c.donor_count||0} donors</p>
        )}
      </div>
      <form onSubmit={donate} className="card p-6 mt-4" data-testid="donation-form">
        <p className="overline mb-3">Donate any amount</p>
        <div className="flex gap-2 mb-3">
          {[5, 10, 25, 50, 100].map(v => (
            <button key={v} type="button" onClick={()=>setAmount(String(v))} className={`text-sm px-3 py-2 rounded-full ${String(v)===amount ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E3] text-[#4A4A4A]"}`} data-testid={`donate-preset-${v}`}>${v}</button>
          ))}
        </div>
        <input type="number" min="1" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} className="input mb-3" placeholder="Any amount" data-testid="donate-amount"/>
        {!user && (
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Your name" className="input" data-testid="donate-name"/>
            <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Your email" className="input" data-testid="donate-email" required/>
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="donate-submit">{busy ? "Opening…" : <><Heart size={14}/> Donate ${(Number(amount)||0).toFixed(2)} <ArrowRight size={14}/></>}</button>
        <div className="mt-3 p-3 rounded-xl bg-[#F2F2F0] text-xs" data-testid="donation-breakdown">
          <div className="flex justify-between"><span>Donation</span><span>${(Number(amount)||0).toFixed(2)}</span></div>
          <div className="flex justify-between text-[#4A4A4A] mt-1"><span>Hoomanz Tip (2 %)</span><span>+${((Number(amount)||0)*0.02).toFixed(2)}</span></div>
          <div className="flex justify-between font-heading text-sm mt-1.5 pt-1.5 border-t border-[#E5E5E3]"><span>You pay</span><span>${((Number(amount)||0)*1.02).toFixed(2)}</span></div>
          <p className="text-[10px] text-[#8A8A8A] mt-1">The cause receives 100 % (${(Number(amount)||0).toFixed(2)}).</p>
        </div>
      </form>
      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={share} className="btn-secondary" data-testid="charity-share"><Share2 size={14}/> Share</button>
        {isOwn && <Link to={`/charities/new?edit=${c.id}`} className="btn-ghost text-sm" data-testid="charity-edit"><Edit3 size={13}/> Edit</Link>}
        {isOwn && <button onClick={remove} className="btn-ghost text-[#B84B4B] text-sm" data-testid="charity-delete"><Trash2 size={12}/> Delete</button>}
      </div>
    </div>
  );
}
